'use memo';

import {
  MigrationRunDetails,
  MigrationRunDetailsStatus,
  useGetMigrations,
  useInvalidateUseGetMigrations,
  useStartMigrationRun,
} from '@urbanx/agx.support.client';
import {
  AgxBadge,
  AgxButton,
  AgxColumn,
  AgxTable,
  AgxTypography,
  IColumnConfig,
  Icon_Refresh,
  useToast,
} from '@urbanx/agx.ui-components2';
import Panel from 'components/panel/Panel';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import SupportBackButton from '../SupportBackButton';

const tableColumns: (string | IColumnConfig)[] = [
  { name: 'Migration', maxColWidth: '45%' },
  { name: 'Status', shrink: true },
  { name: 'Last Run', shrink: true },
  { name: 'Error', maxColWidth: '35%' },
  { name: 'Action', shrink: true, align: 'right' },
];

const statusConfig = {
  [MigrationRunDetailsStatus.NotRun]: {
    label: 'Not run',
    colour: 'lightgrey',
  },
  [MigrationRunDetailsStatus.InProgress]: {
    label: 'In progress',
    colour: 'blue',
  },
  [MigrationRunDetailsStatus.Successful]: {
    label: 'Successful',
    colour: 'green',
  },
  [MigrationRunDetailsStatus.Failed]: {
    label: 'Failed',
    colour: 'red',
  },
} as const;

const formatDateTime = (date?: Date) => (date ? dayjs(date).format('DD/MM/YYYY h:mm A') : '-');

const getMigrationStatus = (migration: MigrationRunDetails) =>
  migration.status ?? MigrationRunDetailsStatus.NotRun;

const getMigrationError = (migration: MigrationRunDetails) =>
  migration.errorMessage || migration.additionalInformation || migration.resultLog;

const Migrations = () => {
  const { pushToast } = useToast();
  const [pendingMigrationTitle, setPendingMigrationTitle] = useState<string>();

  const {
    data: migrations,
    isPending: isLoadingMigrations,
    isError: hasMigrationsError,
  } = useGetMigrations();

  const invalidateMigrations = useInvalidateUseGetMigrations();

  const { startMigrationRun, startMigrationRunPending } = useStartMigrationRun({
    onSuccess: async () => {
      setPendingMigrationTitle(undefined);
      await invalidateMigrations();
      pushToast({
        message: 'Migration run queued',
        type: 'success',
      });
    },
    onError: () => {
      setPendingMigrationTitle(undefined);
      pushToast({
        message: 'Error queueing migration run',
        type: 'error',
      });
    },
  });

  const hasInProgressMigration = useMemo(
    () =>
      (migrations ?? []).some(
        (migration) => getMigrationStatus(migration) === MigrationRunDetailsStatus.InProgress
      ),
    [migrations]
  );

  useEffect(() => {
    if (!hasInProgressMigration) return;

    const intervalId = window.setInterval(() => {
      void invalidateMigrations();
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [hasInProgressMigration, invalidateMigrations]);

  const queueMigrationRun = async (migrationTitle?: string) => {
    setPendingMigrationTitle(migrationTitle ?? 'all');
    await startMigrationRun(
      migrationTitle
        ? {
            migrationTitle,
          }
        : {}
    );
  };

  const rows = useMemo(
    () =>
      (migrations ?? []).map((migration, index) => {
        const status = getMigrationStatus(migration);
        const config = statusConfig[status];
        const errorMessage = getMigrationError(migration);
        const migrationTitle = migration.title ?? '';
        const isPending = startMigrationRunPending && pendingMigrationTitle === migrationTitle;

        return {
          Migration: (
            <AgxColumn gap="spacing-xs">
              <AgxTypography
                id={`support-migration-${index}-display-name`}
                type="body"
                size="small"
              >
                {migration.displayName ?? migrationTitle}
              </AgxTypography>
              <AgxTypography id={`support-migration-${index}-title`} type="body_ui" size="small">
                {migrationTitle}
              </AgxTypography>
            </AgxColumn>
          ),
          Status: (
            <AgxBadge type="colourfill" size="sm" text={config.label} colour={config.colour} />
          ),
          'Last Run': (
            <AgxTypography id={`support-migration-${index}-last-run`} type="body_ui" size="small">
              {formatDateTime(migration.lastRunStartedAt)}
            </AgxTypography>
          ),
          Error: (
            <AgxTypography id={`support-migration-${index}-error`} type="body_ui" size="small">
              {status === MigrationRunDetailsStatus.Failed && errorMessage ? errorMessage : '-'}
            </AgxTypography>
          ),
          Action: (
            <AgxButton
              id={`support-migration-${index}-run`}
              text={isPending ? 'Queueing' : 'Run'}
              variant="hollow"
              size="small"
              leftIcon={<Icon_Refresh width={16} height={16} />}
              disabled={!migration.canRun || startMigrationRunPending}
              onClick={() => void queueMigrationRun(migrationTitle)}
            />
          ),
        };
      }),
    [migrations, pendingMigrationTitle, startMigrationRunPending]
  );

  return (
    <AgxColumn gap="spacing-xl" fill>
      <SupportBackButton />
      <Panel
        title="Migrations"
        actionContent={
          <AgxButton
            id="support-migrations-run-all"
            text={pendingMigrationTitle === 'all' ? 'Queueing' : 'Run all'}
            variant="primary"
            size="small"
            leftIcon={<Icon_Refresh width={16} height={16} />}
            disabled={startMigrationRunPending}
            onClick={() => void queueMigrationRun()}
          />
        }
      >
        <AgxColumn gap="spacing-lg" fill>
          {hasMigrationsError && (
            <AgxTypography id="support-migrations-error" type="body" size="small">
              Unable to load migrations.
            </AgxTypography>
          )}

          <AgxTable
            columns={tableColumns}
            rows={rows}
            showLoadingState={isLoadingMigrations}
            noDataLabel="No migrations found"
          />
        </AgxColumn>
      </Panel>
    </AgxColumn>
  );
};

export default Migrations;
